import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { replaceInquirySelection } from '@/api/inquiries';
import {
  cachedDescendants,
  ensureDescendants,
  locateItem,
} from '@/features/documents/data/libraryData';
import type { ComparisonSelectionTotals } from '@/types/chat/chat';
import {
  fromStored,
  itemKey,
  parseItemKey,
  toPayload,
  type ItemKey,
} from '../selection/selectionSet';
import { toIds } from '../selection/useSourceSelection';

/**
 * The selected guidelines & context of a Q&A as removable chips (spec §3):
 * selected folders and the documents no selected folder covers, as the
 * source dialog shows them. A deleted source is struck through until the
 * deletion is final. Removing a chip saves the selection at once; it
 * applies to the next question only.
 */

export interface SourceChip {
  key: ItemKey;
  kind: 'folder' | 'file';
  id: string;
  /** Null when the name could not be read (shown as #id). */
  name: string | null;
  /** Deleted (inside or after its Undo window). */
  removed: boolean;
}

interface InquiryLike {
  folders?: unknown;
  selection_file_ids?: unknown;
  selection_version?: number;
  selection_totals?: ComparisonSelectionTotals | null;
}

/**
 * The chips for a stored selection, deleted items marked. A deleted
 * document is no longer in `selection_file_ids` (the API leaves soft-deleted
 * documents out), so it comes from `selection_totals.excluded`, which still
 * names it until the deletion is final. That list also holds the documents
 * of selected folders (stored expanded), which never had a chip of their
 * own: a deleted document gets one only if it was selected on its own. The
 * API says so (`directly_selected`, prompt 17), also after a reload; an
 * older API does not, and then it is known only when no folder is selected
 * or when it was a chip before (`wasChip`).
 */
export const chipsFor = (
  keys: ItemKey[],
  totals: ComparisonSelectionTotals | null | undefined,
  folderNames: Map<string, string | null | undefined>,
  wasChip: (key: ItemKey) => boolean = () => false,
  folderSelected = keys.some(key => parseItemKey(key).kind === 'folder'),
): SourceChip[] => [
  ...keys.map((key): SourceChip => {
    const ref = parseItemKey(key);
    if (ref.kind === 'folder') {
      const name = folderNames.get(ref.id);
      // undefined: not found anywhere in the library; null: lookup failed.
      return {
        key,
        kind: 'folder',
        id: ref.id,
        name: name ?? null,
        removed: name === undefined && folderNames.has(ref.id),
      };
    }
    const file = totals?.files?.find(item => String(item.id) === ref.id);
    const excluded = totals?.excluded?.find(item => String(item.id) === ref.id);
    return {
      key,
      kind: 'file',
      id: ref.id,
      name: file?.name ?? excluded?.name ?? null,
      removed: !file && excluded?.reason === 'deleted',
    };
  }),
  ...(totals?.excluded ?? [])
    .filter(
      item =>
        item.reason === 'deleted' &&
        !keys.includes(itemKey('file', String(item.id))) &&
        (typeof item.directly_selected === 'boolean'
          ? item.directly_selected
          : !folderSelected || wasChip(itemKey('file', String(item.id)))),
    )
    .map(
      (item): SourceChip => ({
        key: itemKey('file', String(item.id)),
        kind: 'file',
        id: String(item.id),
        name: item.name,
        removed: true,
      }),
    ),
];

/** The chip keys each Q&A showed in this session. */
const shownChips = new Map<string, Set<ItemKey>>();

export const useSourceChips = ({
  inquiryId,
  inquiry,
  onChanged,
}: {
  inquiryId: string | undefined;
  inquiry: InquiryLike | null | undefined;
  /** The selection was saved: read the Q&A again. */
  onChanged: () => void;
}) => {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<ItemKey | null>(null);
  const [failed, setFailed] = useState(false);
  const folderIds = toIds(inquiry?.folders);
  const fileIds = toIds(inquiry?.selection_file_ids);
  const descendantsOf = useCallback(
    (id: string) => cachedDescendants(queryClient, id),
    [queryClient],
  );

  const query = useQuery({
    queryKey: [
      'chat',
      'source-chips',
      inquiryId ?? '',
      folderIds.join(','),
      fileIds.join(','),
    ],
    enabled: !!inquiryId && folderIds.length + fileIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        await ensureDescendants(queryClient, folderIds);
      } catch {
        // Unknown contents: every stored document stays its own chip.
      }
      const keys = [...fromStored(folderIds, fileIds, descendantsOf)];
      const names = new Map<string, string | null | undefined>();
      await Promise.all(
        folderIds.map(async id => {
          try {
            const found = await locateItem(queryClient, { kind: 'folder', id });
            names.set(id, found?.folder?.name ?? undefined);
          } catch {
            names.set(id, null);
          }
        }),
      );
      // Folders before documents, each in stored order.
      keys.sort(
        (a, b) =>
          Number(parseItemKey(a).kind !== 'folder') -
          Number(parseItemKey(b).kind !== 'folder'),
      );
      return { keys, names };
    },
  });

  const shown = inquiryId ? shownChips.get(inquiryId) : undefined;
  const chips = chipsFor(
    folderIds.length + fileIds.length === 0 ? [] : (query.data?.keys ?? []),
    inquiry?.selection_totals,
    query.data?.names ?? new Map(),
    key => !!shown?.has(key),
    folderIds.length > 0,
  );
  if (inquiryId && query.data) {
    const known = shownChips.get(inquiryId) ?? new Set<ItemKey>();
    query.data.keys.forEach(key => known.add(key));
    shownChips.set(inquiryId, known);
  }

  const remove = useCallback(
    async (key: ItemKey): Promise<boolean> => {
      // Only deleted documents left: nothing to resolve, the rest is empty.
      const stored =
        folderIds.length + fileIds.length === 0 ? [] : query.data?.keys;
      if (!inquiryId || !stored) return false;
      setRemoving(key);
      setFailed(false);
      try {
        let base = Number(inquiry?.selection_version ?? 0);
        // Writing the selection without the item removes it; a deleted
        // document is already missing from the stored keys.
        let current = new Set(stored);
        for (let attempt = 0; attempt < 2; attempt += 1) {
          current.delete(key);
          const response = await replaceInquirySelection(inquiryId, {
            base_version: base,
            ...toPayload(current, descendantsOf),
          });
          if (!response?.success || !response.data) {
            setFailed(true);
            return false;
          }
          if (response.data.status !== 'conflict') {
            onChanged();
            return true;
          }
          // Changed elsewhere meanwhile: remove it from the newer one.
          base = Number(response.data.selection_version ?? 0);
          const folders = toIds(response.data.folder_ids);
          await ensureDescendants(queryClient, folders).catch(() => null);
          current = fromStored(
            folders,
            toIds(response.data.file_ids),
            descendantsOf,
          );
        }
        setFailed(true);
        onChanged();
        return false;
      } finally {
        setRemoving(null);
      }
    },
    [
      descendantsOf,
      fileIds.length,
      folderIds.length,
      inquiry,
      inquiryId,
      onChanged,
      query.data,
      queryClient,
    ],
  );

  return {
    chips,
    loading: query.isLoading,
    removing,
    failed,
    remove,
  };
};
